/** SEO for the email-first homepage, rendered on the server and on navigation. */

import { isProduction } from "@/config/config";
import { EMAIL_LANDING, LANDING } from "@/content/marketing";
import {
  BAND_PRICE,
  FREE_LIMIT,
  MILLION,
  figure,
  unitWord,
} from "@/content/marketing/pricing";
import { PUBLISHER_NODE, SITE_URL } from "@/lib/marketing-jsonld";

export const LANDING_DOCUMENT_TITLE = "GoodSender: Email expertise. A blog built on it.";

/*
 * Meta descriptions carry a ~155 char SERP truncation budget. The budget is
 * pinned by `__tests__/seo-route-heads.test.ts`, which measures the description
 * every route head actually emits rather than trusting a comment, so the next
 * copy edit fails instead of silently truncating.
 *
 * Search and social previews lead with the email foundation.
 */
const LANDING_DESCRIPTION =
  "Your blog, newsletter, and transactional email on one sending engine. Built by the team behind SendGrid and Laneful.";

const LANDING_TWITTER_DESCRIPTION = LANDING_DESCRIPTION;
const LANDING_OG_IMAGE = `${SITE_URL}og-image.png?v=email-first-20261023`;
const LANDING_OG_IMAGE_ALT =
  "GoodSender — Email expertise. A blog built on it. Blog & newsletter · Transactional & marketing email.";

/*
 * The landing page's structured data, here rather than in the route for the
 * same reason /pricing's and /faq's are: a route module may only export
 * components, so a graph defined inside one is a graph no test can read.
 */
export const landingPageJsonLd = {
  "@context": "https://schema.org",
  "@type": "SoftwareApplication",
  name: "GoodSender",
  applicationCategory: "BusinessApplication",
  applicationSubCategory: "Email, Blog & Newsletter Platform",
  operatingSystem: "Web",
  description:
    "Your blog, newsletter, and transactional email on one sending engine. Built by the team behind SendGrid and Laneful.",
  url: SITE_URL,
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "USD",
    description: `Free up to ${figure(FREE_LIMIT)} emails/month. $${BAND_PRICE}/month to ${figure(MILLION)}, then $${BAND_PRICE} per additional ${unitWord(MILLION)}.`,
  },
  creator: {
    "@type": "Organization",
    name: "Joy Labs Ventures",
    url: "https://joylabs.com",
    founder: {
      "@type": "Person",
      name: "Isaac Saldana",
      description: "Co-founder of SendGrid",
    },
  },
  /*
   * Composed from the product and deliverability cards `/` renders:
   * structured data that describes content absent from the page reads to a
   * crawler as hidden markup (PR #1238 review).
   */
  featureList: [
    ...EMAIL_LANDING.products.map((i) => `${i.name}: ${i.body}`),
    ...EMAIL_LANDING.ai.features.map((i) => `${i.name}: ${i.body}`),
    ...LANDING.proof.points.map((p) => `${p.name} ${p.body}`),
  ],
};

/**
 * `#organization` in full — the one page that declares the node rather than
 * referencing it.
 *
 * Spread from `PUBLISHER_NODE`, not hand-written alongside it: this body and
 * the sub-pages' already disagreed (`url` was "https://goodsender.com" here
 * and "https://goodsender.com/" there), which is exactly the by-`@id` merge
 * hazard the shared node exists to close — and the graph test could not see it
 * while the body lived in the route (#792 review). `sameAs` stays here,
 * because it belongs with the node that declares the profiles.
 */
export const landingOrganizationJsonLd = {
  "@context": "https://schema.org",
  ...PUBLISHER_NODE,
  // Populate sameAs with real profile URLs as they come online.
  sameAs: [
    "https://github.com/goodsender",
    "https://www.linkedin.com/company/goodsender",
  ],
};

export const founderPersonJsonLd = {
  "@context": "https://schema.org",
  "@type": "Person",
  "@id": "https://goodsender.com/#isaac-saldana",
  name: "Isaac Saldana",
  jobTitle: "Founder, Joy Labs Ventures · Co-founder, SendGrid",
  worksFor: { "@id": PUBLISHER_NODE["@id"] },
  // sameAs should only include profiles or authoritative pages for Isaac Saldana.
  // Keep company/topic references represented by role or organization properties instead.
  sameAs: [
    "https://www.linkedin.com/in/isaacsaldana/",
    "https://www.crunchbase.com/person/isaac-saldana",
  ],
};

type HeadElement = { type: "meta" | "link"; props: Record<string, string> };

const LANDING_SEO_HEAD_ELEMENTS: HeadElement[] = [
  {
    type: "meta",
    props: { name: "description", content: LANDING_DESCRIPTION },
  },
  {
    type: "meta",
    props: {
      name: "robots",
      content: isProduction
        ? "index, follow, max-image-preview:large"
        : "noindex, nofollow",
    },
  },
  {
    type: "link",
    props: { rel: "canonical", href: "https://goodsender.com/" },
  },
  { type: "meta", props: { property: "og:type", content: "website" } },
  {
    type: "meta",
    props: { property: "og:url", content: "https://goodsender.com/" },
  },
  {
    type: "meta",
    props: {
      property: "og:title",
      content: LANDING_DOCUMENT_TITLE,
    },
  },
  {
    type: "meta",
    props: { property: "og:description", content: LANDING_DESCRIPTION },
  },
  {
    type: "meta",
    props: {
      property: "og:image",
      content: LANDING_OG_IMAGE,
    },
  },
  { type: "meta", props: { property: "og:image:width", content: "1200" } },
  { type: "meta", props: { property: "og:image:height", content: "630" } },
  { type: "meta", props: { property: "og:image:alt", content: LANDING_OG_IMAGE_ALT } },
  { type: "meta", props: { property: "og:site_name", content: "GoodSender" } },
  {
    type: "meta",
    props: { name: "twitter:card", content: "summary_large_image" },
  },
  {
    type: "meta",
    props: { name: "twitter:title", content: LANDING_DOCUMENT_TITLE },
  },
  {
    type: "meta",
    props: {
      name: "twitter:description",
      content: LANDING_TWITTER_DESCRIPTION,
    },
  },
  {
    type: "meta",
    props: {
      name: "twitter:image",
      content: LANDING_OG_IMAGE,
    },
  },
  { type: "meta", props: { name: "twitter:image:alt", content: LANDING_OG_IMAGE_ALT } },
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms.txt",
      title: "LLM Summary",
    },
  },
  {
    type: "link",
    props: {
      rel: "alternate",
      type: "text/plain",
      href: "https://goodsender.com/llms-full.txt",
      title: "LLM Full Reference",
    },
  },
  {
    type: "link",
    props: {
      rel: "describedby",
      type: "application/json",
      href: "https://goodsender.com/api/openapi.json",
    },
  },
  // NOTE: Blog RSS link removed — /blog/feed.xml was returning homepage HTML
  // (SPA fallback), causing broken-feed signals. Re-add once the blog ships.
];

/**
 * Route-level `head()` for the landing page — the SSR-era replacement for the
 * prerender/sync pair: TanStack Start renders these into the SSR HTML per
 * request and keeps them in sync on SPA navigation, so no client-side head
 * patching is needed (216d / #221).
 */
export function landingRouteHead() {
  return {
    meta: [
      { title: LANDING_DOCUMENT_TITLE },
      ...LANDING_SEO_HEAD_ELEMENTS.filter((e) => e.type === "meta").map(
        (e) => e.props,
      ),
    ],
    links: LANDING_SEO_HEAD_ELEMENTS.filter((e) => e.type === "link").map(
      (e) => e.props,
    ),
  };
}
